// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The application shell.
 *
 * Three columns: sources and the object list on the left, the panel canvas in
 * the middle, the inspector and settings on the right. Everything happens in
 * this tab — board files are read with the File API, and the spec leaves as a
 * download.
 *
 * The canvas is a **preview**. It is a careful reimplementation of the engine's
 * geometry, checked against it, but `sigmaKi.py` is what actually builds the
 * panel; the status bar and the About dialog both say so.
 */

import { Board } from '../model/index.ts';
import { buildDisplayList, layerLabel, layerStyle, UNSUPPORTED_LAYER } from '../render/index.ts';
import { extractOutlines } from '../panel/outline.ts';
import { computePreview, previewProblems, previewWarnings, type PanelPreview } from '../panel/preview.ts';
import { analyzeSource, listAnnotations, outlinesToPath, type SourceItem } from '../panel/sources.ts';
import { suggestTabs, type TabFinding } from '../panel/autotabs.ts';
import { emitYaml, parseSpecFile, YamlError } from '../panel/yaml.ts';
import { emptySpec, type PanelSpec, type TabEntry, type Vec2 } from '../panel/spec.ts';
import { BRAND, LOGO_FULL, LOGO_MARK, SPLASH_MS } from './brand.ts';
import { clear, h, makeDropTarget, nextPaint, plural, readFile, saveFile, sectionScrollTarget, svgIcon } from './dom.ts';
import { renderInspector, renderSettings, withFocusRestored } from './inspector.ts';
import { PanelView, type Tool } from './panelview.ts';
import { basename, Session, uniqueBoardName, type Selection, type SourceFile } from './state.ts';

/** Header height, matching `--panel-head-h`. Only a fallback when unmeasurable. */
const PANEL_HEADER_PX = 33;

/** How far a layer chip must travel before a click becomes a drag. */
const LAYER_DRAG_PX = 4;

/**
 * Where a chip dragged to `x` belongs, given the midpoints of **the other**
 * chips: the count of those now to its left.
 *
 * Measured against the others rather than all of them so the result is an index
 * into the list with the dragged chip already removed — which is exactly what
 * `moveItem` wants, and avoids an off-by-one when dragging rightwards.
 */
export function dropIndex(otherMidpoints: readonly number[], x: number): number {
  let i = 0;
  while (i < otherMidpoints.length && x > otherMidpoints[i]!) i++;
  return i;
}

/** Move one item of a list to another index, keeping the rest in order. */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = [...list];
  if (from < 0 || from >= out.length) return out;
  const [item] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(out.length, to)), 0, item as T);
  return out;
}

/** `HH:MM:SS`, for the console. Local time: these are notes to the user, not a log file. */
function clockTime(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

interface AppOptions {
  /** Tests skip the splash. */
  splash?: boolean;
  /**
   * Tests skip the first-run notice: it is a modal, and a modal swallows the
   * keyboard shortcuts everything else is trying to exercise.
   */
  welcome?: boolean;
}

/** Remembers that the first-run notice has been read. */
const WELCOME_SEEN = 'sigmaki.welcome.seen';

/** Zoom factor of one press of the +/− buttons. */
const ZOOM_STEP = 1.5;

/** Width below which the app starts with both sidebars folded. Matches styles.css. */
const NARROW_SCREEN = '(max-width: 700px)';

function narrowScreen(): boolean {
  return typeof matchMedia === 'function' && matchMedia(NARROW_SCREEN).matches;
}

export class App {
  private readonly root: HTMLElement;
  private readonly session = new Session();
  private view: PanelView | null = null;

  private tool: Tool = 'select';
  private readonly toolButtons = new Map<Tool, HTMLButtonElement>();
  /** +Frame / +TB / +LR: mutually exclusive, so all three go together. */
  private readonly outerRailButtons: HTMLButtonElement[] = [];
  private crosshair = false;
  private snap = 0.5;
  /** Auto tabs: roughly one tab per this many millimetres of shared edge. */
  private tabSpacing = 50;
  private visibleLayers = new Set<string>();
  private layersPinned = false;
  /** User's stacking order, bottom first; null means the palette's own. */
  private layerOrder: string[] | null = null;
  /** Set while a chip drag is in flight, so the closing click is not a toggle. */
  private layerDragMoved = false;
  private preview: PanelPreview;
  private drawerTab: 'console' | 'yaml' = 'console';
  private drawerOpen = false;
  /** Sidebars folded to a thin rail. A phone screen starts with the panel, not the sidebars. */
  private leftCollapsed = narrowScreen();
  private rightCollapsed = narrowScreen();
  private log: Array<{ text: string; level: 'info' | 'warn' | 'bad'; at: string }> = [];

  private els: {
    sources: HTMLElement;
    objects: HTMLElement;
    inspector: HTMLElement;
    settings: HTMLElement;
    tabs: HTMLElement;
    layerBar: HTMLElement;
    watermark: HTMLImageElement;
    status: HTMLElement;
    coords: HTMLElement;
    drawerBody: HTMLElement;
    drawer: HTMLElement;
    sourceCount: HTMLElement;
    objectCount: HTMLElement;
    railBadges: HTMLElement;
    autoTabs: HTMLButtonElement;
    missing: HTMLElement;
    undo: HTMLButtonElement;
    redo: HTMLButtonElement;
    specButton: HTMLButtonElement;
    save: HTMLButtonElement;
    crosshair: HTMLButtonElement;
  };

  private dialog: HTMLElement | null = null;
  private dialogOnClose: (() => void) | null = null;
  private overlay: HTMLElement | null = null;

  constructor(root: HTMLElement, options: AppOptions = {}) {
    this.root = root;
    this.preview = computePreview(this.session.spec, this.session.provider());

    const left = h('div', { class: 'col col-left' });
    const main = h('div', { class: 'col col-main' });
    const right = h('div', { class: 'col col-right' });
    const overlay = h('div', { class: 'overlay' },
      h('div', { class: 'overlay-card' },
        h('div', { class: 'spinner' }),
        h('div', { class: 'overlay-text' })));
    clear(root).append(left, main, right, overlay);
    this.overlay = overlay;

    this.els = {
      sources: h('div', { class: 'panel-body' }),
      objects: h('div', { class: 'panel-body' }),
      inspector: h('div', { class: 'panel-body' }),
      settings: h('div', { class: 'panel-body' }),
      tabs: h('div', { class: 'panel-tabs' }),
      layerBar: h('div', { class: 'layer-bar' }),
      watermark: h('img', { src: LOGO_FULL, alt: '', class: 'watermark on' }),
      status: h('span', { class: 'status' }, 'no boards loaded'),
      coords: h('span', { class: 'coords mono' }),
      drawerBody: h('pre', {}),
      drawer: h('div', { class: 'drawer' }),
      sourceCount: h('span', { class: 'count' }),
      objectCount: h('span', { class: 'count' }),
      railBadges: h('div', { class: 'rail-indicators' }),
      missing: h('div', { class: 'missing' }),
      undo: h('button', { class: 'sm', title: 'Undo (Ctrl+Z)' }, '↶ Undo'),
      redo: h('button', { class: 'sm', title: 'Redo (Ctrl+Y)' }, '↷ Redo'),
      specButton: h('button', { class: 'sm spec-button' }),
      crosshair: h('button', {
        class: 'sm',
        title: 'Full-canvas crosshairs on the pointer',
      }, '✛ Crosshair'),
      save: h('button', { class: 'sm primary' }, 'Save YAML…'),
      autoTabs: h('button', { class: 'sm auto-tabs' }, 'Auto tabs'),
    };

    this.buildLeft(left);
    this.buildMain(main);
    this.buildRight(right);
    this.bindKeys();
    this.renderCollapsed();

    this.stackHeaders(left);
    this.stackHeaders(right);
    // The brand header's height is only known once it has laid out, and the
    // stack offsets are measured from it.
    new ResizeObserver(() => {
      this.stackHeaders(left);
      this.stackHeaders(right);
    }).observe(left);

    this.renderAll();

    const welcome = (): void => {
      if (options.welcome !== false) this.maybeShowWelcome();
    };
    if (options.splash === false) welcome();
    else this.showSplash(welcome);
  }

  // --- layout -----------------------------------------------------------------------

  private buildLeft(col: HTMLElement): void {
    col.append(
      h(
        'div',
        { class: 'title-bar pinned-top' },
        h('img', { src: LOGO_MARK, alt: '', class: 'brand-mark' }),
        h(
          'div',
          { class: 'brand-text' },
          h('h1', {}, BRAND.short),
          h('p', { class: 'tagline' }, BRAND.tagline),
        ),
        h('button', {
          class: 'sm about-button',
          title: `About ${BRAND.name}`,
          onclick: () => this.showAbout(),
        }, 'About'),
        this.collapseButton('left', false),
      ),
      this.rail('left', this.els.railBadges),
    );

    const drop = h('div', { class: 'dropzone' },
      h('div', { class: 'note' }, 'Drop .kicad_pcb files here, or click to choose'));
    // A spec dropped here works too, rather than being rejected for being the
    // wrong kind of file.
    makeDropTarget(drop, '.kicad_pcb,.yaml,.yml,.json', (files) => void this.loadBoards(files), true);

    const sources = h('div', {},
      h('div', { class: 'panel-body' }, this.els.missing, drop), this.els.sources);

    col.append(
      this.section('Sources', this.els.sourceCount, sources),
      this.section('Objects', this.els.objectCount, this.els.objects),
    );
  }

  private buildMain(col: HTMLElement): void {
    const canvas = h('canvas');
    // Anything that tracks the pointer lives here, so following the cursor does
    // not cost a repaint of the whole board.
    const overlayCanvas = h('canvas', { class: 'pointer-layer' });
    const toolbar = h('div', { class: 'map-toolbar' });

    const tools: Array<[Tool, string, string]> = [
      ['select', 'Select', 'Select, move and resize things  (S)'],
      ['tab', 'Tab', 'Click in a gap to bridge it; drag to aim, Shift for a free angle  (T)'],
      ['rect', 'Rail rect', 'Drag out a rectangular rail  (R)'],
      ['text', 'Text', 'Click to place silkscreen text on the panel  (X)'],
    ];
    const seg = h('div', { class: 'seg' });
    for (const [tool, label, tip] of tools) {
      const button = h('button', { class: 'sm', title: tip }, label);
      button.addEventListener('click', () => {
        this.tool = tool;
        this.renderAll();
      });
      // Kept so the active tool can be re-marked: the toolbar is built once,
      // so setting the class here alone would leave it stuck on the first tool.
      this.toolButtons.set(tool, button);
      seg.append(button);
    }
    toolbar.append(seg);

    this.els.crosshair.addEventListener('click', () => {
      this.crosshair = !this.crosshair;
      this.renderAll();
    });
    toolbar.append(this.els.crosshair);

    this.els.undo.addEventListener('click', () => {
      if (this.session.undo()) this.renderAll();
    });
    this.els.redo.addEventListener('click', () => {
      if (this.session.redo()) this.renderAll();
    });
    toolbar.append(h('div', { class: 'seg' }, this.els.undo, this.els.redo));

    for (const [label, tip, make] of [
      ['+Frame', 'A frame right around the panel, 5 mm wide',
        () => ({ type: 'frame' as const, width: 5, hspace: 3, vspace: 3, corner_cuts: 'none' as const })],
      ['+TB rails', 'Rails along the top and bottom edges',
        () => ({ type: 'tb' as const, thickness: 5, space: 3 })],
      ['+LR rails', 'Rails along the left and right edges',
        () => ({ type: 'lr' as const, thickness: 5, space: 3 })],
    ] as const) {
      const button = h('button', { class: 'sm', title: tip }, label);
      this.outerRailButtons.push(button);
      button.addEventListener('click', () => {
        this.commit((spec) => {
          spec.rails = [...(spec.rails ?? []), make()];
        });
        this.session.select({ kind: 'rail', index: (this.session.spec.rails?.length ?? 1) - 1 });
        this.renderAll();
      });
      toolbar.append(button);
    }

    const spacingInput = h('input', {
      type: 'number', class: 'snap', step: '1', min: '1', value: String(this.tabSpacing),
      title: 'Auto tabs: roughly one tab per this many mm of shared edge',
    });
    spacingInput.addEventListener('change', () => {
      this.tabSpacing = Math.max(1, Number(spacingInput.value) || 50);
      spacingInput.value = String(this.tabSpacing);
    });
    this.els.autoTabs.addEventListener('click', () => this.autoTabs());
    toolbar.append(h('div', { class: 'seg' }, this.els.autoTabs, spacingInput),
      h('span', { class: 'note' }, 'mm'));

    const snapInput = h('input', { type: 'number', class: 'snap', step: '0.1', min: '0', value: String(this.snap) });
    snapInput.addEventListener('change', () => {
      this.snap = Math.max(0, Number(snapInput.value) || 0);
      this.renderAll();
    });
    snapInput.title = 'Round positions to this grid; 0 for none';
    toolbar.append(h('span', { class: 'spacer' }),
      h('label', { title: 'Round positions to this grid; 0 for none' }, 'snap'),
      snapInput, h('span', { class: 'note' }, 'mm'));

    const fit = h('button', { class: 'sm', title: 'Zoom to the whole panel  (F)' }, 'Fit');
    fit.addEventListener('click', () => {
      this.view?.fit();
      this.view?.requestDraw();
    });
    toolbar.append(fit);

    // A spec is loaded through a hidden file input rather than the Sources
    // dropzone: it is a different kind of thing, and it needs to be findable.
    const specInput = h('input', { type: 'file', accept: '.yaml,.yml,.json' });
    specInput.style.display = 'none';
    specInput.addEventListener('change', () => {
      const file = specInput.files?.[0];
      if (file) void this.loadSpecFile(file);
      specInput.value = '';
    });
    this.els.specButton.addEventListener('click', () => specInput.click());
    this.els.save.addEventListener('click', () => void this.download());
    toolbar.append(specInput, this.els.specButton, this.els.save);

    col.append(this.els.tabs, toolbar, this.els.layerBar,
      h('div', { class: 'map-wrap' }, canvas, overlayCanvas, this.els.watermark,
        // There is no wheel on a phone, and pinching is not the only way people zoom.
        h('div', { class: 'zoom-controls' },
          h('button', { class: 'zoom-in', title: 'Zoom in', 'aria-label': 'Zoom in',
            onclick: () => this.view?.zoomBy(ZOOM_STEP) }, '+'),
          h('button', { class: 'zoom-out', title: 'Zoom out', 'aria-label': 'Zoom out',
            onclick: () => this.view?.zoomBy(1 / ZOOM_STEP) }, '−'))));

    // The drawer sits above the status bar, which stays pinned to the bottom.
    this.buildDrawer(col);

    col.append(
      h('div', { class: 'view-footer' },
        this.els.status,
        this.els.coords,
        h('span', { class: 'note credit' }, 'created by ',
          h('a', { href: BRAND.companyUrl, target: '_blank', rel: 'noopener noreferrer' }, BRAND.company)),
      ),
    );

    try {
      this.view = new PanelView(canvas, overlayCanvas);
    } catch (e) {
      // Without a 2D context there is no canvas, but the spec editor, the
      // inspector and the download all still work — so report it and carry on
      // rather than taking the whole tool down with the drawing.
      this.note(`canvas unavailable: ${e instanceof Error ? e.message : String(e)}`, 'bad');
      return;
    }

    this.view.onSelect = (selection) => {
      this.session.select(selection);
      this.renderSidePanels();
      this.renderObjects();
      // The canvas has to be told too, or the highlight and the resize handles
      // never appear — which also leaves the handles unhittable, so resizing a
      // rail silently does nothing.
      this.view?.setInput(this.viewInput());
    };
    // A drag records its undo point once, at the start, then edits the spec
    // live so the canvas keeps up. The commit on release adds no second point.
    // Written straight to the element: a re-render on every mouse move would
    // undo the point of the separate overlay canvas.
    this.view.onPointerAt = (at) => {
      this.els.coords.textContent =
        at === null ? '' : `${at[0].toFixed(3)}, ${at[1].toFixed(3)} mm`;
    };
    this.view.onEditBegin = () => this.session.snapshot();
    this.view.onCommit = (change) => {
      this.session.apply(change);
      this.renderAll();
    };
    this.view.onPreviewChange = (change) => {
      this.session.apply(change);
      this.refreshPreview();
      this.view?.setInput(this.viewInput());
    };
    this.view.onNewTab = (origin, direction) => {
      this.commit((spec) => {
        spec.tabs = [...(spec.tabs ?? []), { origin, direction }];
      });
      this.session.select({ kind: 'tab', index: (this.session.spec.tabs?.length ?? 1) - 1 });
      this.tool = 'select';
      this.renderAll();
    };
    this.view.onNewRect = (at, size) => {
      this.commit((spec) => {
        spec.rails = [...(spec.rails ?? []), { type: 'rect', at, size }];
      });
      this.session.select({ kind: 'rail', index: (this.session.spec.rails?.length ?? 1) - 1 });
      this.tool = 'select';
      this.renderAll();
    };
    this.view.onNewText = (at) => {
      const text = globalThis.prompt?.('Text:');
      if (text === null || text === undefined || text === '') {
        this.tool = 'select';
        this.renderAll();
        return;
      }
      this.commit((spec) => {
        spec.text = [...(spec.text ?? []), { text, at, layer: 'F.SilkS', height: 1.5 }];
      });
      this.session.select({ kind: 'text', index: (this.session.spec.text?.length ?? 1) - 1 });
      this.tool = 'select';
      this.renderAll();
    };

    canvas.addEventListener('dragover', (ev) => ev.preventDefault());
    canvas.addEventListener('drop', (ev) => {
      ev.preventDefault();
      const raw = ev.dataTransfer?.getData('text/plain');
      if (!raw) return;
      try {
        const { file, id } = JSON.parse(raw) as { file: string; id: string };
        this.placeSource(file, id, this.view!.worldAtClient(ev.clientX, ev.clientY));
      } catch {
        // Not one of ours — a stray drag from elsewhere.
      }
    });
  }

  private buildDrawer(col: HTMLElement): void {
    const head = h('div', { class: 'drawer-head' });
    const seg = h('div', { class: 'seg' });
    for (const tab of ['console', 'yaml'] as const) {
      const button = h('button', { class: this.drawerTab === tab ? 'sm on' : 'sm' }, tab);
      button.addEventListener('click', () => {
        this.drawerTab = tab;
        this.drawerOpen = true;
        this.renderDrawer();
      });
      seg.append(button);
    }
    const toggle = h('button', { class: 'sm' }, '▾');
    toggle.addEventListener('click', () => {
      this.drawerOpen = !this.drawerOpen;
      this.renderDrawer();
    });
    head.append(seg, h('span', { class: 'spacer' }), toggle);

    this.els.drawer.append(head, h('div', { class: 'drawer-body' }, this.els.drawerBody));
    col.append(this.els.drawer);
  }

  private buildRight(col: HTMLElement): void {
    const inspector = this.section('Inspector', null, this.els.inspector);
    // The right column has no title bar of its own, so its first heading carries the fold.
    inspector.querySelector('h2')!.append(this.collapseButton('right', false));
    col.append(
      this.rail('right', h('div', { class: 'rail-indicators' })),
      inspector,
      this.section('Panel settings', null, this.els.settings),
    );
  }

  /**
   * The button that folds a sidebar to its rail, or opens it again. Each side
   * has two: one in the open sidebar and one on the rail.
   */
  private collapseButton(side: 'left' | 'right', collapsed: boolean): HTMLElement {
    const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
    return h('button', {
      class: 'sm icon collapse-toggle',
      title: label,
      'aria-label': label,
      'aria-expanded': String(!collapsed),
      onclick: (ev: Event) => {
        // Inside a section heading, a click would otherwise also scroll to it.
        ev.stopPropagation();
        this.setCollapsed(side, !collapsed);
      },
    }, svgIcon(side === 'left' ? 'M2 3h12v10H2z M6 3v10' : 'M2 3h12v10H2z M10 3v10', 14));
  }

  /** A folded sidebar: the logo, the way back out, and a few status badges. */
  private rail(side: 'left' | 'right', indicators: HTMLElement): HTMLElement {
    return h('div', { class: `rail rail-${side}` },
      h('img', { src: LOGO_MARK, alt: BRAND.short, class: 'rail-logo' }),
      this.collapseButton(side, true),
      indicators);
  }

  private setCollapsed(side: 'left' | 'right', collapsed: boolean): void {
    if (side === 'left') this.leftCollapsed = collapsed;
    else this.rightCollapsed = collapsed;
    // A phone has room for the canvas and at most one sidebar.
    if (!collapsed && narrowScreen()) {
      if (side === 'left') this.rightCollapsed = true;
      else this.leftCollapsed = true;
    }
    this.renderCollapsed();
  }

  private renderCollapsed(): void {
    this.root.classList.toggle('left-collapsed', this.leftCollapsed);
    this.root.classList.toggle('right-collapsed', this.rightCollapsed);
  }

  // --- long operations --------------------------------------------------------------

  /** Show the overlay directly: a full re-render would itself block. */
  private setBusy(message: string | undefined): void {
    const overlay = this.overlay;
    if (overlay === null) return;
    overlay.classList.toggle('on', message !== undefined);
    const text = overlay.querySelector('.overlay-text');
    if (text) text.textContent = message ?? '';
  }

  /**
   * Show the overlay, let the browser paint it, then do the work.
   *
   * The yield is the whole point. JavaScript is single-threaded, so setting a
   * flag and calling the work on the next line means the frame never reaches
   * the screen and the tab simply freezes — which is what parsing a 33 MB board
   * does. Waiting for a paint guarantees the overlay is visible first.
   */
  private async busyRun<T>(message: string, work: () => T): Promise<T> {
    this.setBusy(message);
    await nextPaint();
    try {
      return work();
    } finally {
      this.setBusy(undefined);
    }
  }

  // --- loading ----------------------------------------------------------------------

  private async loadBoards(files: File[]): Promise<void> {
    for (const file of files) {
      if (/\.(ya?ml|json)$/i.test(file.name)) {
        await this.loadSpecFile(file);
        continue;
      }
      const size = file.size > 2e6 ? ` (${(file.size / 1e6).toFixed(0)} MB)` : '';
      try {
        this.setBusy(`Reading ${file.name}${size}…`);
        await nextPaint();
        const contents = await readFile(file);

        // Parsing and outline extraction are the expensive part — seconds on a
        // large multi-board file — and they hold the thread while they run, so
        // the overlay has to be on screen before they start.
        const warnings: string[] = [];
        const entry = await this.busyRun(`Parsing ${file.name}${size}…`, (): SourceFile => {
          const board = Board.parse(contents, file.name);
          const outlines = extractOutlines(board);
          const analysis = analyzeSource(board, file.name);
          warnings.push(...outlines.warnings, ...analysis.warnings);
          return {
            name: basename(file.name),
            board,
            displayList: buildDisplayList(board),
            polygons: outlines.polygons,
            annotations: listAnnotations(board),
            analysis,
            maxEdgeWidth: outlines.maxEdgeWidth,
            bytes: file.size,
          };
        });

        this.session.addSource(entry);
        for (const warning of warnings) this.note(`${entry.name}: ${warning}`, 'warn');
        this.note(
          `loaded ${entry.name} — ${plural(entry.analysis.items.length, 'board')}, ` +
            `${plural(entry.board.objects.length, 'object')}`,
        );
        if (!this.layersPinned) this.visibleLayers = this.suggestedLayers();
      } catch (e) {
        this.note(`${file.name}: ${e instanceof Error ? e.message : String(e)}`, 'bad');
      } finally {
        this.setBusy(undefined);
      }
    }
    this.renderAll();
  }

  private async loadSpecFile(file: File): Promise<void> {
    try {
      const text = await readFile(file);
      const spec = parseSpecFile(text, file.name);
      this.session.loadSpec(file.name, spec);
      this.note(`loaded spec ${file.name}`);

      const missing = this.missingFiles();
      if (missing.length > 0) {
        this.note(`this spec needs ${missing.join(', ')} — see Sources`, 'warn');
      }
      if (/(^|\n)\s*#/.test(text)) {
        this.note('comments in this file are not preserved when saving again', 'warn');
      }
      this.renderAll();
      this.view?.fit();
    } catch (e) {
      const where = e instanceof YamlError ? ` (${e.message})` : '';
      this.note(`${file.name}: could not read spec${where}`, 'bad');
      if (!(e instanceof YamlError)) {
        this.note(String(e instanceof Error ? e.message : e), 'bad');
      }
      this.renderAll();
    }
  }

  private placeSource(file: string, id: string, position: Vec2): void {
    const source = this.session.sources.get(basename(file));
    const item = source?.analysis.items.find((i) => i.id === id);
    if (source === undefined || item === undefined) return;

    this.commit((spec) => {
      spec.boards = [...(spec.boards ?? []), {
        name: uniqueBoardName(spec, item.name),
        file: source.name,
        source: item.source,
        position,
        anchor: 'center',
        rotation: 0,
      }];
    });
    this.session.select({ kind: 'board', index: (this.session.spec.boards?.length ?? 1) - 1 });
    this.renderAll();
  }

  private async download(): Promise<void> {
    const panel = this.session.active;
    const problems = previewProblems(this.preview);
    if (problems.length > 0) {
      this.note(`downloading with ${plural(problems.length, 'unresolved problem')}`, 'warn');
    }
    const fileName = panel.fileName ?? `${panel.name}.yaml`;
    try {
      // Where the file actually went depends on the browser: Chromium opens a
      // Save-As dialog, Firefox has no such API and simply downloads. Saying
      // "saved" either way would send Firefox users looking in the wrong place.
      const how = await saveFile(fileName, emitYaml(panel.spec));
      panel.fileName = fileName;
      panel.dirty = false;
      this.note(
        how === 'saved'
          ? `saved ${fileName} — run: sigmaKi.py ${fileName}`
          : `downloaded ${fileName} — put it beside your board files and run: sigmaKi.py ${fileName}`,
      );
    } catch (e) {
      if ((e as DOMException)?.name !== 'AbortError') {
        this.note(`could not save: ${e instanceof Error ? e.message : String(e)}`, 'bad');
      }
    }
    this.renderTabs();
    this.renderSpecButton();
    this.renderDrawer();
  }

  // --- editing ----------------------------------------------------------------------

  private commit(change: (spec: PanelSpec) => void): void {
    this.session.mutate(change);
    this.refreshPreview();
  }

  /** Suggest tabs, asking first what to do with any that are already there. */
  private autoTabs(): void {
    const count = (this.session.spec.tabs ?? []).length;
    if (count === 0) {
      this.placeTabs(false);
      return;
    }

    const choose = (keep: boolean): void => {
      this.closeDialog();
      this.placeTabs(keep);
    };
    this.openDialog('Auto tabs',
      h('div', { class: 'dialog-body' },
        h('p', {}, `The panel already has ${plural(count, 'tab')}.`),
        h('p', { class: 'note' },
          'Replace them with a fresh set, or keep them and add tabs only to the gaps that have none.')),
      h('div', { class: 'dialog-foot' },
        h('button', { class: 'cancel', onclick: () => this.closeDialog() }, 'Cancel'),
        h('button', { class: 'keep', onclick: () => choose(true) }, 'Keep and add'),
        h('button', { class: 'primary replace', onclick: () => choose(false) }, 'Replace existing')));
  }

  private placeTabs(keep: boolean): void {
    const spec = this.session.spec;
    const existing = keep ? spec.tabs ?? [] : [];
    const { tabs, findings } = suggestTabs(this.preview, {
      spacing: this.tabSpacing,
      defaults: spec.defaults,
      existing,
    });
    this.commit((s) => {
      s.tabs = [...existing, ...tabs];
    });
    if (!keep && this.session.active.selection?.kind === 'tab') this.session.select(null);

    const heading = `Placed ${plural(tabs.length, 'tab')}`;
    this.note(`auto tabs: ${heading.toLowerCase()}`);
    this.renderAll();
    if (findings.length > 0) this.showFindings(heading, findings);
  }

  /**
   * Adds a suggested rail, drops the long tabs it replaces and tabs the new gaps,
   * as one undo step. The analysis then runs again: one rail can make other
   * suggestions pointless, and two suggestions for the same channel can overlap.
   */
  private addSuggestedRail(finding: TabFinding): void {
    const rail = finding.rail;
    if (rail === undefined) return;
    const flagged = finding.tabs ?? [];
    const same = (a: TabEntry, b: TabEntry): boolean =>
      a.origin[0] === b.origin[0] && a.origin[1] === b.origin[1]
      && a.direction[0] === b.direction[0] && a.direction[1] === b.direction[1];

    this.commit((s) => {
      s.rails = [...(s.rails ?? []), rail];
      s.tabs = (s.tabs ?? []).filter((t) => !flagged.some((f) => same(f, t)));
    });
    if (this.session.active.selection?.kind === 'tab') this.session.select(null);

    const { tabs, findings } = suggestTabs(this.preview, {
      spacing: this.tabSpacing,
      defaults: this.session.spec.defaults,
      existing: this.session.spec.tabs ?? [],
    });
    // `apply`, not `commit`: the added tabs belong to the rail's undo step.
    this.session.apply((s) => {
      s.tabs = [...(s.tabs ?? []), ...tabs];
    });

    const heading = `Added a rail and ${plural(tabs.length, 'tab')}`;
    this.note(`auto tabs: ${heading.toLowerCase()}`);
    this.renderAll();
    this.showFindings(heading, findings);
  }

  /** What auto tabs could not do well, with a way to add each suggested rail. */
  private showFindings(heading: string, findings: TabFinding[]): void {
    for (const f of findings) this.note(`auto tabs: ${f.message}`, 'warn');

    const rows = findings.map((f) => h('li', { class: `finding ${f.kind}` },
      h('span', {}, f.message),
      f.rail === undefined ? null : h('button', {
        class: 'sm add-rail',
        title: `Add a rect rail at [${f.rail.at!.join(', ')}], ${f.rail.size!.join(' × ')} mm`,
        onclick: () => this.addSuggestedRail(f),
      }, 'Add rail')));

    this.openDialog('Auto tabs',
      h('div', { class: 'dialog-body' },
        h('p', {}, h('strong', {}, heading)),
        findings.length === 0
          ? h('p', { class: 'note' }, 'Nothing left to report.')
          : h('ul', { class: 'findings' }, ...rows)),
      h('div', { class: 'dialog-foot' },
        h('button', { class: 'primary close', onclick: () => this.closeDialog() }, 'Close')));
  }

  private openDialog(label: string, ...children: HTMLElement[]): HTMLElement {
    this.closeDialog();
    const panel = h('div', { class: 'dialog', role: 'dialog', 'aria-label': label }, ...children);
    const backdrop = h('div', {
      class: 'dialog-backdrop',
      onclick: (ev: MouseEvent) => {
        if (ev.target === backdrop) this.closeDialog();
      },
    }, panel);
    this.dialog = backdrop;
    this.root.append(backdrop);
    panel.querySelector<HTMLElement>('button.primary')?.focus();
    return panel;
  }

  private refreshPreview(): void {
    this.preview = computePreview(this.session.spec, this.session.provider());
  }

  // --- rendering --------------------------------------------------------------------

  private renderAll(): void {
    this.refreshPreview();
    this.renderTabs();
    this.renderMissing();
    this.renderSources();
    this.renderLayerBar();
    this.renderSidePanels();
    this.renderDrawer();
    this.renderStatus();

    for (const [tool, button] of this.toolButtons) {
      button.classList.toggle('on', this.tool === tool);
    }
    // A panel gets its outer boundary from one frame or one set of edge rails;
    // a second would be measured against the first and land outside it.
    const hasOuterRail = (this.session.spec.rails ?? []).some(
      (r) => r?.type === 'frame' || r?.type === 'tb' || r?.type === 'lr',
    );
    for (const button of this.outerRailButtons) {
      button.disabled = hasOuterRail;
      if (hasOuterRail) button.title = 'The panel already has a frame or edge rails';
    }
    this.els.crosshair.classList.toggle('on', this.crosshair);

    // Tabs hold boards to something; until a rail exists, the boards have
    // nothing around them worth guessing at.
    const hasRail = this.preview.rails.some((r) => r.error === null && r.polys.length > 0);
    const hasBoard = this.preview.boards.some((b) => b.error === null);
    this.els.autoTabs.disabled = !hasRail || !hasBoard;
    this.els.autoTabs.title = !hasBoard ? 'Place a board first'
      : !hasRail ? 'Add a frame or rails first'
      : 'Place tabs across every gap between boards, rails and frame';

    const panel = this.session.active;
    this.els.undo.disabled = panel.undo.length === 0;
    this.els.redo.disabled = panel.redo.length === 0;
    this.renderSpecButton();

    const empty = (this.session.spec.boards ?? []).length === 0;
    this.els.watermark.classList.toggle('on', empty);
    this.view?.setInput(this.viewInput());
  }

  private viewInput(): Parameters<PanelView['setInput']>[0] {
    return {
      preview: this.preview,
      spec: this.session.spec,
      sources: this.session.sources,
      selection: this.session.active.selection,
      visibleLayers: this.visibleLayers,
      layerOrder: this.paintOrder(),
      crosshair: this.crosshair,
      tool: this.tool,
      snap: this.snap,
    };
  }

  private renderTabs(): void {
    const bar = clear(this.els.tabs);
    for (const panel of this.session.panels) {
      const tab = h('div', { class: panel.id === this.session.activeId ? 'panel-tab on' : 'panel-tab' });
      tab.append(h('span', {}, panel.name));
      if (panel.dirty) tab.append(h('span', { class: 'dirty', title: 'unsaved changes' }, '●'));
      tab.addEventListener('click', () => {
        this.session.activeId = panel.id;
        this.renderAll();
        this.view?.fit();
      });

      const close = h('button', { class: 'close sm', title: 'Close panel' }, '×');
      close.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (panel.dirty && !globalThis.confirm?.(`Close ${panel.name}? Unsaved changes are lost.`)) return;
        this.session.closePanel(panel.id);
        this.renderAll();
      });
      tab.append(close);
      bar.append(tab);
    }

    const add = h('button', { class: 'sm add', title: 'New panel' }, '+');
    add.addEventListener('click', () => {
      this.session.addPanel();
      this.renderAll();
    });
    bar.append(add);
  }

  /**
   * Give each section header its slot in the sticky stack.
   *
   * Headers pin at `top: i × H` and `bottom: (n − 1 − i) × H`, so the ones you
   * have scrolled past stack upward and the ones you have not reached stack
   * downward. Every heading stays on screen, and the content between the two
   * stacks is the section you are reading.
   *
   * The `.panel` wrapper generates no box (`display: contents`), which is what
   * lets a header pin to the scrolling column rather than to its own section —
   * without that it would vanish the moment its section scrolled past.
   */
  private stackHeaders(col: HTMLElement): void {
    // Anything pinned above the sections — here the brand header — takes room
    // off the top of the stack.
    let above = 0;
    for (const pinned of [...col.querySelectorAll(':scope > .pinned-top')] as HTMLElement[]) {
      pinned.style.setProperty('top', `${above}px`);
      above += pinned.getBoundingClientRect().height;
    }
    col.style.setProperty('--stack-top-offset', `${above}px`);

    const panels = ([...col.querySelectorAll(':scope > .panel')] as HTMLElement[])
      .filter((panel) => panel.querySelector(':scope > h2') !== null);
    panels.forEach((panel, i) => {
      panel.style.setProperty('--panel-above', String(i));
      panel.style.setProperty('--panel-below', String(panels.length - 1 - i));
    });
  }

  /**
   * Bring a section to rest just below the headers pinned above it.
   *
   * Not `scrollIntoView`: the section wrapper generates no box, and the target
   * is not the top of the column but the underside of the pinned stack.
   */
  private scrollToSection(wrap: HTMLElement): void {
    const col = wrap.closest('.col') as HTMLElement | null;
    const content = wrap.querySelector(':scope > .panel-content') as HTMLElement | null;
    const head = wrap.querySelector(':scope > h2') as HTMLElement | null;
    if (!col || !content || !head) return;

    col.scrollTop = sectionScrollTarget({
      scrollTop: col.scrollTop,
      columnTop: col.getBoundingClientRect().top,
      contentTop: content.getBoundingClientRect().top,
      headersAbove: Number(wrap.style.getPropertyValue('--panel-above') || '0'),
      headerHeight: head.getBoundingClientRect().height || PANEL_HEADER_PX,
    });
  }

  /** A collapsible, scroll-to section with a pinned header. */
  private section(title: string, count: HTMLElement | null, body: HTMLElement): HTMLElement {
    const head = h('h2', {}, h('span', { class: 'panel-title' }, title));
    if (count !== null) head.append(count);

    const wrap = h('section', { class: 'panel' }, head,
      h('div', { class: 'panel-content' }, body));
    head.addEventListener('click', () => this.scrollToSection(wrap));
    return wrap;
  }

  /**
   * The spec control: which file this panel is, and whether it has moved on
   * from what is on disk.
   *
   * Once a spec has been loaded the button carries its name rather than a
   * generic label, so the panel you are looking at is identified rather than
   * merely editable. Saving is offered only when there is something to save.
   */
  private renderSpecButton(): void {
    const panel = this.session.active;
    const button = clear(this.els.specButton);

    if (panel.fileName === undefined) {
      button.append('Load spec…');
      button.title = 'Load a panel specification';
    } else {
      button.append(h('span', { class: 'mono' }, panel.fileName));
      if (panel.dirty) button.append(h('span', { class: 'dirty', title: 'unsaved changes' }, '●'));
      button.title = `${panel.fileName}${panel.dirty ? ' — modified' : ''}\nClick to load a different spec`;
    }
    button.classList.toggle('loaded', panel.fileName !== undefined);

    this.els.save.disabled = !panel.dirty;
    this.els.save.title = panel.dirty
      ? 'Save the specification'
      : 'Nothing has changed since the last save';
  }

  /** Board files the current spec names but which are not loaded. */
  private missingFiles(): string[] {
    const wanted = new Set<string>();
    for (const board of this.session.spec.boards ?? []) {
      const name = basename(board?.file ?? '');
      if (name !== '' && !this.session.sources.has(name)) wanted.add(name);
    }
    return [...wanted].sort();
  }

  /**
   * A spec is only half the panel: it names board files by name, and the tool
   * cannot go and fetch them. So when one is loaded, say plainly which files it
   * still needs and take them right there.
   */
  private renderMissing(): void {
    const into = clear(this.els.missing);
    const missing = this.missingFiles();
    if (missing.length === 0) {
      this.els.missing.style.display = 'none';
      return;
    }
    this.els.missing.style.display = '';

    into.append(
      h('div', { class: 'missing-head' },
        `This spec needs ${plural(missing.length, 'board file')}:`),
      h('ul', { class: 'missing-list' },
        ...missing.map((name) => h('li', { class: 'mono' }, name))),
    );

    const drop = h('div', { class: 'dropzone' },
      h('div', { class: 'note' }, 'Drop them here, or click to choose'));
    makeDropTarget(drop, '.kicad_pcb', (files) => void this.loadBoards(files), true);
    into.append(drop);
  }

  private renderSources(): void {
    const into = clear(this.els.sources);
    this.els.sourceCount.textContent = String(this.session.sources.size);

    if (this.session.sources.size === 0) {
      into.append(h('p', { class: 'empty' }, 'no boards loaded'));
      return;
    }

    for (const source of this.session.sources.values()) {
      const card = h('div', { class: 'src-card' });
      const remove = h('button', { class: 'sm', title: 'Remove' }, '×');
      remove.addEventListener('click', () => {
        this.session.removeSource(source.name);
        this.renderAll();
      });
      card.append(h('header', {},
        h('span', { class: 'grow' }, source.name),
        h('span', { class: 'size' }, `${(source.bytes / 1e6).toFixed(1)}M`),
        remove));

      for (const item of source.analysis.items) {
        card.append(this.sourceRow(source.name, item));
      }
      into.append(card);
    }
  }

  private sourceRow(file: string, item: SourceItem): HTMLElement {
    const [x0, y0, x1, y1] = item.bbox;
    const row = h('div', {
      class: 'src-item',
      draggable: 'true',
      title: 'double-click or drag onto the panel',
    });

    const margin = 1;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '42');
    svg.setAttribute('height', '34');
    svg.setAttribute('viewBox', `${x0 - margin} ${y0 - margin} ${x1 - x0 + margin * 2} ${y1 - y0 + margin * 2}`);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', outlinesToPath(item.outlines));
    path.setAttribute('fill', '#2b7d3f66');
    path.setAttribute('stroke', '#58c878');
    path.setAttribute('stroke-width', String(Math.max((x1 - x0) / 60, 0.2)));
    path.setAttribute('fill-rule', 'evenodd');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.append(path);

    row.append(svg,
      h('span', { class: 'name' }, item.name),
      h('span', { class: 'size' }, `${(x1 - x0).toFixed(0)}×${(y1 - y0).toFixed(0)}`));

    row.addEventListener('dragstart', (ev) => {
      ev.dataTransfer?.setData('text/plain', JSON.stringify({ file, id: item.id }));
    });
    row.addEventListener('dblclick', () => {
      this.placeSource(file, item.id, this.view?.viewCentre() ?? [0, 0]);
    });
    return row;
  }

  /**
   * The layers as chips: the stack itself, topmost on the left.
   *
   * This is the paint order reversed, not KiCad's Appearance-panel order. The
   * two are different — Edge.Cuts is listed low in the Appearance panel but is
   * painted above everything — and since dragging a chip restacks the board,
   * the row has to *be* the stack or the gesture means nothing. Reading it
   * front-to-back still puts the front-side layers on the left: F.Cu left of
   * B.Cu, each silkscreen left of the copper it sits on.
   *
   * Once the user has rearranged them their order wins, with any layer they
   * have not seen yet — a board loaded later may bring new ones — falling in
   * after the ones they have arranged.
   */
  private uiLayers(): string[] {
    const seen = new Set<string>();
    for (const source of this.session.sources.values()) {
      for (const layer of source.displayList.layers) {
        if (layer !== UNSUPPORTED_LAYER) seen.add(layer);
      }
    }
    // Descending paint order: the layer drawn last is the one on top.
    const topFirst = (a: string, b: string): number => layerStyle(b).order - layerStyle(a).order;
    if (this.layerOrder === null) return [...seen].sort(topFirst);

    const rank = new Map(this.layerOrder.map((l, i) => [l, i]));
    const known = [...seen].filter((l) => rank.has(l)).sort((a, b) => rank.get(a)! - rank.get(b)!);
    const fresh = [...seen].filter((l) => !rank.has(l)).sort(topFirst);
    return [...known, ...fresh];
  }

  /**
   * Paint order: bottom of the stack first, because later is drawn on top.
   * The chip bar reads top-first, so this is simply its reverse.
   */
  private paintOrder(): string[] {
    return [...this.uiLayers()].reverse();
  }

  /** Copper, silkscreen and the board outline: the useful starting set. */
  private suggestedLayers(): Set<string> {
    return new Set(this.uiLayers().filter((l) => layerStyle(l).visibleByDefault));
  }

  /**
   * Let a chip be dragged sideways to restack its layer.
   *
   * The chip is moved in the DOM as the pointer passes each neighbour, and the
   * new order is read back from the bar on release — re-rendering mid-drag
   * would destroy the element holding the pointer capture.
   */
  private makeLayerDraggable(chip: HTMLElement, bar: HTMLElement): void {
    chip.addEventListener('pointerdown', (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      // A fresh press: whatever a previous drag left behind is no longer true.
      this.layerDragMoved = false;

      const chips = [...bar.querySelectorAll<HTMLElement>('.chip')];
      const from = chips.indexOf(chip);
      if (from === -1) return;

      // Measured once, at the start. The chip is never moved in the DOM during
      // the drag: doing that shifts every midpoint underneath the pointer and
      // the chip oscillates between two positions instead of following it.
      const others = chips.filter((c) => c !== chip);
      const midpoints = others.map((c) => {
        const r = c.getBoundingClientRect();
        return r.left + r.width / 2;
      });

      const startX = ev.clientX;
      let dragging = false;
      let to = from;

      const clearMarkers = (): void => {
        for (const c of chips) c.classList.remove('drop-before', 'drop-after');
      };

      const move = (m: PointerEvent): void => {
        if (!dragging && Math.abs(m.clientX - startX) < LAYER_DRAG_PX) return;
        if (!dragging) {
          dragging = true;
          this.layerDragMoved = true;
          chip.classList.add('dragging');
        }
        // The chip follows the pointer; only the marker says where it will land.
        chip.style.transform = `translateX(${m.clientX - startX}px)`;

        to = dropIndex(midpoints, m.clientX);
        clearMarkers();
        if (to < others.length) others[to]!.classList.add('drop-before');
        else others[others.length - 1]?.classList.add('drop-after');
      };

      const doc = chip.ownerDocument;
      const up = (): void => {
        doc.removeEventListener('pointermove', move as EventListener);
        doc.removeEventListener('pointerup', up);
        doc.removeEventListener('pointercancel', up);
        chip.style.transform = '';
        chip.classList.remove('dragging');
        clearMarkers();
        if (!dragging) return;

        this.layerOrder = moveItem(
          chips.map((c) => c.dataset['layer'] ?? ''),
          from,
          to,
        ).filter((name) => name !== '');
        this.renderLayerBar();
        this.view?.setInput(this.viewInput());
      };

      // On the document, so the drag survives leaving the chip, and so nothing
      // depends on pointer capture — which not every environment implements.
      doc.addEventListener('pointermove', move as EventListener);
      doc.addEventListener('pointerup', up);
      doc.addEventListener('pointercancel', up);
    });
  }

  private setLayers(layers: Set<string>): void {
    this.layersPinned = true;
    this.visibleLayers = layers;
    this.renderLayerBar();
    this.view?.setInput(this.viewInput());
  }

  private renderLayerBar(): void {
    const bar = clear(this.els.layerBar);
    const available = this.uiLayers();
    if (available.length === 0) {
      bar.append(h('span', { class: 'note' }, 'board layers appear here once a file is loaded'));
      return;
    }

    const all = h('button', { class: 'sm', title: 'Show every layer these boards use' }, 'All');
    all.addEventListener('click', () => this.setLayers(new Set(available)));

    const reset = h('button', {
      class: 'sm',
      title: 'Back to copper, silkscreen and the board outline',
    }, 'Reset');
    reset.addEventListener('click', () => {
      // Reset means "put it back": stacking as well as visibility.
      this.layerOrder = null;
      this.setLayers(this.suggestedLayers());
    });

    bar.append(all, reset,
      h('span', { class: 'note layer-hint' },
        h('kbd', {}, 'shift'), ' solo  ', h('kbd', {}, 'ctrl'), ' all'));

    for (const layer of available) {
      const on = this.visibleLayers.has(layer);
      const chip = h('button', {
        class: on ? 'chip on' : 'chip',
        'data-layer': layer,
        title: `${layerLabel(layer)}\nClick to toggle · Shift+click to show only this · ` +
          `Ctrl+click to show all\nDrag sideways to restack — the leftmost layer draws on top`,
      });
      const dot = h('i', { class: 'dot' });
      dot.style.background = layerStyle(layer).color;
      chip.append(dot, h('span', {}, layerLabel(layer)));

      this.makeLayerDraggable(chip, bar);

      chip.addEventListener('click', (ev: MouseEvent) => {
        // A drag ends with a click on the same element; that click is not a
        // request to toggle the layer.
        if (this.layerDragMoved) {
          this.layerDragMoved = false;
          return;
        }
        // Isolating one layer and getting everything back are the two moves
        // worth a shortcut: checking one inner layer against the outline means
        // switching off a dozen chips one at a time otherwise.
        if (ev.shiftKey) {
          this.setLayers(new Set([layer]));
        } else if (ev.ctrlKey || ev.metaKey) {
          this.setLayers(new Set(available));
        } else {
          const next = new Set(this.visibleLayers);
          if (next.has(layer)) next.delete(layer);
          else next.add(layer);
          this.setLayers(next);
        }
      });
      bar.append(chip);
    }
  }

  private renderSidePanels(): void {
    this.renderObjects();
    withFocusRestored(this.els.inspector, () => {
      renderInspector(this.els.inspector, {
        spec: this.session.spec,
        preview: this.preview,
        selection: this.session.active.selection,
        commit: (change) => {
          this.commit(change);
          this.renderAll();
        },
      });
    });
    withFocusRestored(this.els.settings, () => {
      renderSettings(this.els.settings, this.session.spec, (change) => {
        this.commit(change);
        this.renderAll();
      });
    });
  }

  private renderObjects(): void {
    const into = clear(this.els.objects);
    const spec = this.session.spec;
    const groups: Array<[Selection['kind'], string, (item: never, i: number) => string]> = [
      ['board', 'Boards', (b: never, i) => (b as { name?: string }).name ?? `board_${i}`],
      ['rail', 'Rails', (r: never) => {
        const rail = r as { type: string; at?: number[] };
        return rail.type === 'rect' && rail.at ? `${rail.type} @ ${rail.at.join(', ')}` : rail.type;
      }],
      ['tab', 'Tabs', (t: never) => {
        const tab = t as { origin?: number[] };
        return tab.origin ? `@ ${tab.origin.join(', ')}` : '(tab)';
      }],
      ['text', 'Text', (t: never) => (t as { text?: string }).text ?? '(text)'],
    ];

    let total = 0;
    for (const [kind, title, label] of groups) {
      const list = this.session.listFor(kind);
      total += list.length;
      if (list.length === 0) continue;
      into.append(h('div', { class: 'obj-group' }, title));
      list.forEach((item, index) => {
        const selection = this.session.active.selection;
        const selected = selection?.kind === kind && selection.index === index;
        const row = h('div', { class: selected ? 'obj-row sel' : 'obj-row' });
        row.append(h('span', { class: 'label' }, label(item as never, index)));

        const del = h('button', { class: 'del', title: 'Delete' }, '×');
        del.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.commit((s) => {
            const target = kindList(s, kind);
            if (target) target.splice(index, 1);
          });
          this.session.select(null);
          this.renderAll();
        });
        row.append(del);
        row.addEventListener('click', () => {
          this.session.select({ kind, index });
          this.renderAll();
        });
        into.append(row);
      });
    }

    this.els.objectCount.textContent = String(total);
    if (total === 0) into.append(h('p', { class: 'empty' }, 'drag a board onto the canvas'));
    void spec;
  }

  private renderDrawer(): void {
    this.els.drawer.classList.toggle('closed', !this.drawerOpen);
    const body = clear(this.els.drawerBody);

    for (const button of this.els.drawer.querySelectorAll('.seg button')) {
      button.classList.toggle('on', button.textContent === this.drawerTab);
    }

    if (this.drawerTab === 'yaml') {
      body.textContent = emitYaml(this.session.spec);
      return;
    }

    const problems = previewProblems(this.preview);
    // Two kinds of line, and only one of them is an event. Notes happened at a
    // moment and are stamped with it; spec problems describe the panel as it
    // stands right now and are recomputed every render, so a time on them would
    // say when it was drawn rather than when anything happened.
    for (const entry of this.log) {
      body.append(h('div', { class: `log-line log-entry ${entry.level}` },
        h('span', { class: 'stamp' }, entry.at), entry.text));
    }
    for (const problem of problems) {
      body.append(h('div', { class: 'log-line log-issue bad' }, `spec: ${problem}`));
    }
    for (const warning of previewWarnings(this.preview)) {
      body.append(h('div', { class: 'log-line log-issue warn' }, `check: ${warning}`));
    }
    if (this.log.length === 0 && problems.length === 0 &&
        this.preview.clearance.length === 0) {
      body.append(h('div', { class: 'note' }, 'no messages'));
    }
  }

  private renderStatus(): void {
    const problems = previewProblems(this.preview);
    const boards = (this.session.spec.boards ?? []).length;
    const box = this.preview.panelBBox;

    const warnings = previewWarnings(this.preview);
    this.renderRailBadges(problems.length, warnings.length);
    const status = this.els.status;
    status.classList.toggle('bad', problems.length > 0);
    status.classList.toggle('warn', problems.length === 0 && warnings.length > 0);

    if (boards === 0) {
      status.textContent = this.session.sources.size === 0
        ? 'no boards loaded'
        : 'drag a board onto the canvas';
      return;
    }

    const size = box === null
      ? ''
      : ` · panel ${(box.maxX - box.minX).toFixed(1)} × ${(box.maxY - box.minY).toFixed(1)} mm`;
    const trouble = problems.length > 0
      ? ` · ${plural(problems.length, 'problem')}`
      : warnings.length > 0 ? ` · ${plural(warnings.length, 'clearance warning')}` : '';
    status.textContent = `${plural(boards, 'board')}${size}${trouble} · preview only, sigmaKi.py builds the panel`;
  }

  /** What stays visible of the left sidebar when it is folded. */
  private renderRailBadges(problems: number, warnings: number): void {
    const sources = this.session.sources.size;
    const badges = [h('span', { class: 'rail-badge', title: plural(sources, 'source file') }, String(sources))];
    if (problems > 0) {
      badges.push(h('span', { class: 'rail-badge bad', title: plural(problems, 'problem') }, '!'));
    } else if (warnings > 0) {
      badges.push(h('span', { class: 'rail-badge warn', title: plural(warnings, 'clearance warning') }, '!'));
    }
    clear(this.els.railBadges).append(...badges);
  }

  private note(text: string, level: 'info' | 'warn' | 'bad' = 'info'): void {
    this.log.push({ text, level, at: clockTime() });
    if (this.log.length > 200) this.log.shift();
    this.renderDrawer();
  }

  // --- keyboard ---------------------------------------------------------------------

  private bindKeys(): void {
    // The app's own document, not `globalThis`: the two are the same object in
    // a browser but not everywhere, and the app should not depend on that.
    this.root.ownerDocument.addEventListener('keydown', (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      if (ev.key === 'Escape') {
        if (this.dialog !== null) {
          this.closeDialog();
          return;
        }
        this.tool = 'select';
        this.session.select(null);
        this.renderAll();
        return;
      }
      if (this.dialog !== null) return;

      const meta = ev.ctrlKey || ev.metaKey;
      if (meta && ev.key.toLowerCase() === 'z' && !ev.shiftKey) {
        if (this.session.undo()) this.renderAll();
        ev.preventDefault();
        return;
      }
      if (meta && (ev.key.toLowerCase() === 'y' || (ev.key.toLowerCase() === 'z' && ev.shiftKey))) {
        if (this.session.redo()) this.renderAll();
        ev.preventDefault();
        return;
      }
      if (meta) return;

      const selection = this.session.active.selection;
      if ((ev.key === 'Delete' || ev.key === 'Backspace') && selection !== null) {
        this.commit((s) => {
          const list = kindList(s, selection.kind);
          if (list) list.splice(selection.index, 1);
        });
        this.session.select(null);
        this.renderAll();
        ev.preventDefault();
        return;
      }
      // R now picks the rail-rect tool, so rotating a board moves to Shift+R.
      // Shift+Alt+R turns it the other way; the inspector takes any angle.
      if (ev.shiftKey && ev.key.toLowerCase() === 'r' && selection?.kind === 'board') {
        const delta = ev.altKey ? -90 : 90;
        this.commit((s) => {
          const board = s.boards?.[selection.index];
          if (board) board.rotation = (((board.rotation ?? 0) + delta) % 360 + 360) % 360;
        });
        this.renderAll();
        return;
      }
      if (ev.shiftKey) return;

      const shortcut: Record<string, Tool> = { s: 'select', t: 'tab', r: 'rect', x: 'text' };
      const tool = shortcut[ev.key.toLowerCase()];
      if (tool !== undefined) {
        this.tool = tool;
        this.renderAll();
        return;
      }

      if (ev.key === 'f') {
        this.view?.fit();
        this.view?.requestDraw();
      }
    });
  }

  // --- branding ---------------------------------------------------------------------

  /**
   * A moment of the logo before the tool appears.
   *
   * Dismissible by click or key so it never stands between the user and the
   * work, and skipped entirely when the system asks for reduced motion.
   */
  private showSplash(onDone: () => void): void {
    const splash = h('div', { class: 'splash' },
      h('img', { src: LOGO_FULL, alt: `${BRAND.name} — ${BRAND.tagline}`, class: 'splash-logo' }));
    this.root.append(splash);

    let done = false;
    const dismiss = (): void => {
      if (done) return;
      done = true;
      splash.classList.add('leaving');
      setTimeout(() => splash.remove(), 400);
      onDone();
    };
    splash.addEventListener('click', dismiss);
    this.root.ownerDocument.addEventListener('keydown', dismiss, { once: true });

    const reduced = typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(dismiss, reduced ? 0 : SPLASH_MS);
  }

  private closeDialog(): void {
    // Run any parting business first: Escape, the backdrop and the button all
    // land here, and all three should respect "don't show this again".
    const after = this.dialogOnClose;
    this.dialogOnClose = null;
    this.dialog?.remove();
    this.dialog = null;
    after?.();
  }

  /** This page's own storage, rather than whatever `globalThis` happens to be. */
  private get storage(): Storage | undefined {
    return this.root.ownerDocument.defaultView?.localStorage;
  }

  private welcomeSeen(): boolean {
    try {
      return this.storage?.getItem(WELCOME_SEEN) === '1';
    } catch {
      // Storage can be unavailable or blocked; then the notice simply shows.
      return false;
    }
  }

  private rememberWelcomeSeen(): void {
    try {
      this.storage?.setItem(WELCOME_SEEN, '1');
    } catch {
      // Nothing to do: the notice will appear again next time, which is the
      // safe direction to fail in.
    }
  }

  private maybeShowWelcome(): void {
    if (!this.welcomeSeen()) this.showWelcome();
  }

  /**
   * What this tool is, said once, before anyone spends an afternoon in it.
   *
   * The single thing worth knowing up front is that nothing here produces a
   * board: the panel is built by `sigmaKi.py` from the file you take away. It
   * is easy to assume otherwise from a tool that draws the panel on screen, and
   * finding out at the end would be an unpleasant surprise.
   */
  private showWelcome(): void {
    this.closeDialog();

    const dismiss = h('input', { type: 'checkbox', id: 'welcome-dismiss' });
    this.dialogOnClose = (): void => {
      if (dismiss.checked) this.rememberWelcomeSeen();
    };
    const close = (): void => this.closeDialog();

    const panel = h('div', { class: 'dialog', role: 'dialog', 'aria-label': `About ${BRAND.name}` },
      h('div', { class: 'dialog-head' },
        h('img', { src: LOGO_MARK, alt: '', class: 'dialog-logo' }),
        h('div', {}, h('h2', {}, BRAND.name), h('p', { class: 'tagline' }, BRAND.tagline))),
      h('div', { class: 'dialog-body' },
        h('p', {},
          h('strong', {}, 'This page writes the panel specification. '),
          'It does not build the panel itself — what you take away is a YAML file ' +
          'describing the layout, not a board.'),
        h('ol', { class: 'steps' },
          h('li', {}, 'Load your ', h('span', { class: 'mono' }, '.kicad_pcb'),
            ' files here, arrange the boards, add rails and tabs, then ',
            h('strong', {}, 'Save YAML…')),
          h('li', {}, 'Put that file beside your board files and run ',
            h('span', { class: 'mono' }, 'sigmaKi.py panel.yaml'),
            ' to produce the panel')),
        h('p', { class: 'note' },
          'The panel is built locally because it needs KiCad and KiKit installed, ' +
          'and a complex layout can take a lot of memory — more than a browser tab ' +
          'should be asked for. The canvas here is a preview of the same geometry.'),
        h('label', { class: 'checkbox' }, dismiss, h('span', {}, "Don't show this again")),
      ),
      h('div', { class: 'dialog-foot' },
        h('button', { class: 'primary', onclick: close }, 'Got it')),
    );

    const backdrop = h('div', {
      class: 'dialog-backdrop',
      onclick: (ev: MouseEvent) => {
        if (ev.target === backdrop) close();
      },
    }, panel);

    this.dialog = backdrop;
    this.root.append(backdrop);
    panel.querySelector<HTMLElement>('button')?.focus();
  }

  private showAbout(): void {
    this.closeDialog();
    const close = (): void => this.closeDialog();

    const panel = h('div', { class: 'dialog', role: 'dialog', 'aria-label': `About ${BRAND.name}` },
      h('div', { class: 'dialog-head' },
        h('img', { src: LOGO_MARK, alt: '', class: 'dialog-logo' }),
        h('div', {}, h('h2', {}, BRAND.name), h('p', { class: 'tagline' }, BRAND.tagline)),
        h('button', { class: 'sm dialog-close', title: 'Close', onclick: close }, '×')),
      h('div', { class: 'dialog-body' },
        h('p', {}, 'Lay out a KiCad panel visually — place boards from one or more files, ' +
          'add rails and a frame, aim the tabs that hold it together — and take away the ' +
          'YAML specification that builds it.'),
        h('p', { class: 'preview-note' },
          'The canvas is a preview. It reimplements the engine\'s geometry and is checked ' +
          'against it, but ', h('strong', {}, 'sigmaKi.py'), ' is what actually builds the ' +
          'panel. Run it locally on the spec: rendering is memory-hungry, which is exactly ' +
          'why it does not happen in a browser tab.'),
        h('p', {}, 'Created by ', h('strong', {}, BRAND.author), ' ',
          h('a', { href: `mailto:${BRAND.authorEmail}` }, `<${BRAND.authorEmail}>`), '.'),
        h('div', { class: 'row' },
          h('a', { class: 'dialog-link', href: BRAND.docsUrl, target: '_blank', rel: 'noopener noreferrer' }, 'Documentation ↗'),
          h('a', { class: 'dialog-link', href: BRAND.repoUrl, target: '_blank', rel: 'noopener noreferrer' }, 'Source on gitlab ↗')),
        h('p', { class: 'disclaimer' },
          `${BRAND.name} is provided as it stands, without warranty of any kind. `,
          h('strong', {}, BRAND.company),
          ' accepts no liability for any loss or damage arising from its use. Always open ' +
          'the finished panel in KiCad and check it before you send anything to a fabricator.'),
        h('p', { class: 'note' },
          'Everything runs in this tab. Board files are never uploaded, and the tool makes ' +
          'no network requests at all.')),
    );

    const backdrop = h('div', {
      class: 'dialog-backdrop',
      onclick: (ev: MouseEvent) => {
        if (ev.target === backdrop) close();
      },
    }, panel);

    this.dialog = backdrop;
    this.root.append(backdrop);
    panel.querySelector<HTMLElement>('.dialog-close')?.focus();
  }

  // --- for tests --------------------------------------------------------------------

  /** Load a board from text, without a File — the headless path. */
  loadBoardText(name: string, text: string): void {
    const board = Board.parse(text, name);
    const { polygons, maxEdgeWidth } = extractOutlines(board);
    this.session.addSource({
      name: basename(name),
      board,
      displayList: buildDisplayList(board),
      polygons,
      annotations: listAnnotations(board),
      analysis: analyzeSource(board, name),
      maxEdgeWidth,
      bytes: text.length,
    });
    this.renderAll();
  }

  get state(): Session {
    return this.session;
  }

  get currentPreview(): PanelPreview {
    return this.preview;
  }
}

function kindList(spec: PanelSpec, kind: Selection['kind']): unknown[] | undefined {
  switch (kind) {
    case 'board': return spec.boards;
    case 'rail': return spec.rails;
    case 'patch': return spec.patches;
    case 'tab': return spec.tabs;
    case 'text': return spec.text;
  }
}

export { emptySpec };
